import { useCallback, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import type { EntityImageEntrySource, EntityImageTab } from "src/AppEventPublisher";
import type { EntityImageDialogSurface, EntityImageDialogTab } from "../EntityImageDialog";
import type { DeleteImageResponse, SetCoverImageResponse } from "./entityImageApi";
import type { UseEntityImagesResult } from "./useEntityImages";
import type { EntityImageListItem } from "./types";

// Held even while closed so `surface` stays stable through the dialog's exit animation
// instead of flashing the create panes as it fades out.
export type EntityImageSurfaceState = { type: "create"; tab: EntityImageDialogTab } | { type: "viewer"; imageId: string } | { type: "gallery" };

export function useEntityImageDialogState() {
  const [isOpen, setIsOpen] = useState(false);
  const [surface, setSurface] = useState<EntityImageSurfaceState>({ type: "create", tab: "generate" });
  const onOpenChange = useCallback((open: boolean) => {
    if (!open) setIsOpen(false);
  }, []);
  return { isOpen, setIsOpen, surface, setSurface, onOpenChange };
}

export type EntityImageDialogState = ReturnType<typeof useEntityImageDialogState>;

interface UseEntityImageCardParams {
  dialog: EntityImageDialogState;
  entity: { id: string; imageId?: string | null; imageUrl?: string | null };
  images: UseEntityImagesResult;
  removeImage: (imageId: string) => Promise<DeleteImageResponse>;
  setCoverImage: (imageId: string) => Promise<SetCoverImageResponse>;
  /** Applies a new cover (or clears it with nulls) to the card's local entity and any stores. */
  applyCover: (cover: { imageId: string | null; imageUrl: string | null }) => void;
  analytics: {
    dialogOpened: (props: { entrySource: EntityImageEntrySource; initialTab: EntityImageTab; hasExistingCover: boolean }) => void;
    deleted: (props: { imageId: string; wasCover: boolean }) => void;
    coverSet: (props: { imageId: string }) => void;
  };
  /** Prefix for Sentry `feature` tags, e.g. "character_portrait". */
  sentryFeaturePrefix: string;
  setToast: (message: string) => void;
}

/**
 * Card-level orchestration shared by character and worldbuilding cards: which dialog surface to
 * open from each thumbnail, and keeping the card's cover in sync after save/delete/set-cover.
 */
export function useEntityImageCard({ dialog, entity, images, removeImage, setCoverImage, applyCover, analytics, sentryFeaturePrefix, setToast }: UseEntityImageCardParams) {
  const { setIsOpen, setSurface } = dialog;
  const { images: imageList, refetch } = images;
  const [coverImageFailed, setCoverImageFailed] = useState(false);

  useEffect(() => {
    setCoverImageFailed(false);
  }, [entity.imageUrl]);
  const markCoverImageFailed = useCallback(() => setCoverImageFailed(true), []);

  const hasExistingCover = !!entity.imageId || imageList.some((image) => image.status === "COVER");
  const coverImagePrompt = imageList.find((image) => image.status === "COVER")?.prompt;

  const openDialog = useCallback(
    (nextSurface: EntityImageSurfaceState, entrySource: EntityImageEntrySource) => {
      analytics.dialogOpened({ entrySource, initialTab: nextSurface.type === "create" ? nextSurface.tab : nextSurface.type, hasExistingCover });
      setSurface(nextSurface);
      setIsOpen(true);
    },
    [analytics, hasExistingCover, setIsOpen, setSurface],
  );

  const handleCoverClick = useCallback(() => {
    const coverId = entity.imageId || imageList.find((image) => image.status === "COVER")?.id;
    if (coverId) {
      openDialog({ type: "viewer", imageId: coverId }, "cover_click_existing");
    } else {
      openDialog({ type: "create", tab: "generate" }, "cover_click_empty");
    }
  }, [entity.imageId, imageList, openDialog]);

  const handleAddClick = useCallback(() => openDialog({ type: "create", tab: "generate" }, "add_click"), [openDialog]);

  // From inside the already-open gallery surface — just swap surfaces, no fresh dialogOpened event.
  const handleGenerateFromGallery = useCallback(() => setSurface({ type: "create", tab: "generate" }), [setSurface]);

  const handleAlternateClick = useCallback((image: EntityImageListItem) => openDialog({ type: "viewer", imageId: image.id }, "alternate_click"), [openDialog]);

  const handleOverflowClick = useCallback(() => openDialog({ type: "gallery" }, "overflow_click"), [openDialog]);

  const handleImagesSaved = useCallback(
    (coverResult: { imageId: string; imageUrl: string } | null, meta: { hadFailures: boolean }) => {
      if (coverResult) applyCover(coverResult);
      // This also fires on a partial batch failure (to sync cover state), so only close on a full save.
      if (!meta?.hadFailures) setIsOpen(false);
      void refetch();
    },
    [applyCover, setIsOpen, refetch],
  );

  const handleDeleteImage = useCallback(
    async (imageId: string) => {
      try {
        const result = await removeImage(imageId);
        analytics.deleted({ imageId, wasCover: result.wasActive });
        if (result.wasActive) {
          const nextCover = imageList.find((image) => image.id !== imageId && image.status !== "COVER");
          const promoted = nextCover ? await setCoverImage(nextCover.id).catch(() => null) : null;
          applyCover(promoted ? { imageId: promoted.imageId, imageUrl: promoted.imageUrl } : { imageId: null, imageUrl: null });
        }
        await refetch();
      } catch (err) {
        Sentry.captureException(err, { tags: { feature: `${sentryFeaturePrefix}_delete` } });
        setToast("Couldn't remove that image, please try again");
        throw err;
      }
    },
    [removeImage, setCoverImage, imageList, analytics, applyCover, refetch, sentryFeaturePrefix, setToast],
  );

  // Shared by the gallery lightbox's and the Generate tab's "Set thumbnail" actions — both just
  // need the same imageId -> new cover round trip.
  const handleSetCoverImage = useCallback(
    async (imageId: string) => {
      try {
        const { imageId: newImageId, imageUrl } = await setCoverImage(imageId);
        analytics.coverSet({ imageId: newImageId });
        applyCover({ imageId: newImageId, imageUrl });
        await refetch();
      } catch (err) {
        Sentry.captureException(err, { tags: { feature: `${sentryFeaturePrefix}_set_cover` } });
        setToast("Couldn't set that as the thumbnail, please try again");
        throw err;
      }
    },
    [setCoverImage, analytics, applyCover, refetch, sentryFeaturePrefix, setToast],
  );

  const surface = dialog.surface;
  const dialogProps: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    surface: EntityImageDialogSurface;
    initialTab: EntityImageDialogTab;
    initialLightboxImageId: string | null;
    images: EntityImageListItem[];
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    onLoadMore: () => void;
    onDeleteImage: (imageId: string) => Promise<void>;
    onSetCoverImage: (imageId: string) => Promise<void>;
  } = {
    open: dialog.isOpen,
    onOpenChange: dialog.onOpenChange,
    surface: surface.type,
    initialTab: surface.type === "create" ? surface.tab : "generate",
    initialLightboxImageId: surface.type === "viewer" ? surface.imageId : null,
    images: imageList,
    hasNextPage: images.hasNextPage,
    isFetchingNextPage: images.isFetchingNextPage,
    onLoadMore: images.fetchNextPage,
    onDeleteImage: handleDeleteImage,
    onSetCoverImage: handleSetCoverImage,
  };

  return {
    coverImageFailed,
    markCoverImageFailed,
    coverImagePrompt,
    hasExistingCover,
    handleCoverClick,
    handleAddClick,
    handleGenerateFromGallery,
    handleAlternateClick,
    handleOverflowClick,
    handleImagesSaved,
    handleDeleteImage,
    handleSetCoverImage,
    dialogProps,
  };
}
